// COS-MODES (docs/design/2026-10-30-chief-of-staff-model.md §3): Take back on an `agent_directed`
// inbox row. An agent directed the person's agent (Team · Tell me); one click stops it and gives
// the task back (`POST tasks/{id}/take-back` with the `task.agent_directed` event the item came
// from). On success the row says so and the caller marks the item done; on failure the problem's
// detail shows under the button.
import { Button, toast } from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { Undo2Icon } from "lucide-react";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";

export function TakeBackButton({
  api,
  co,
  taskId,
  eventId,
  summary,
  onTakenBack,
}: {
  api: Api;
  co: string;
  taskId: string;
  eventId: string;
  /** The item's line, for the button's name and the toast. */
  summary: string;
  onTakenBack: () => void;
}) {
  const take = useMutation({
    mutationFn: () =>
      api.client.tasks.takeBack({ params: { co, id: taskId }, body: { event_id: eventId } }),
    onSuccess: () => {
      toast.add({ title: "Taken back", description: summary });
      onTakenBack();
    },
  });
  if (take.isSuccess)
    return (
      <span data-slot="taken-back" className="mt-1 flex text-xs text-muted-foreground">
        Taken back
      </span>
    );
  return (
    <span data-slot="take-back" className="mt-1 flex flex-col items-start gap-1">
      <Button
        size="xs"
        variant="outline"
        aria-label={`Take back: ${summary}`}
        disabled={take.isPending}
        onClick={() => take.mutate()}
      >
        <Undo2Icon data-icon="inline-start" />
        {take.isPending ? "Taking back…" : "Take back"}
      </Button>
      {take.error ? (
        <span role="alert" className="text-xs text-danger-text">
          {problemText(take.error, "It could not be taken back.")}
        </span>
      ) : null}
    </span>
  );
}
